import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { AuthenticationService, CredentialsService } from '@app/core';
import { ClientResolverService } from '@app/core/client-resolver.service';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { ReservationService } from '../reservation.service';
import { Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material';
import { FontPickerComponent, FontPickerDirective } from 'ngx-font-picker';
import { environment } from '@env/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-reservation-integration',
  templateUrl: './reservation-integration.component.html',
  styleUrls: ['./reservation-integration.component.scss']
})
export class ReservationIntegrationComponent implements OnInit, OnDestroy {
  activeClient: any = {
    id: null
  };
  widgetUrl: string;
  expandedPanel: string;
  settings: any;
  font: any;
  secondaryFont: any;
  settingsFormGroup: FormGroup;
  frontendUrl: string = '';
  generalResSettings: any = {
    showShiftFirstOnline: false
  };

  widgetCdnUrl = 'https://cdn.gastroguide.de/widgets/reservation/latest/bundle.esm.js';
  widgetCdnUrlClassic = 'https://cdn.gastroguide.de/widgets/reservation/latest/bundle.min.js';
  embedTypeOptions = [
    { value: 'esm', label: 'IntegrationCode.EmbedModern' },
    { value: 'iife', label: 'IntegrationCode.EmbedClassic' }
  ];
  triggerModeOptions = [
    { value: 'fab', label: 'IntegrationCode.TriggerFab' },
    { value: 'button', label: 'IntegrationCode.TriggerButton' }
  ];
  themeOptions = [
    { value: 'trattoria', label: 'Trattoria' },
    { value: 'brasserie', label: 'Brasserie' },
    { value: 'modern', label: 'Modern' }
  ];
  localeOptions = [
    { value: 'de', label: 'Deutsch (de)' },
    { value: 'en', label: 'English (en)' },
    { value: 'es', label: 'Español (es)' },
    { value: 'fr', label: 'Français (fr)' },
    { value: 'it', label: 'Italiano (it)' }
  ];
  fabPositionOptions = [
    { value: 'bottom-right', label: 'IntegrationCode.PositionBottomRight' },
    { value: 'bottom-left', label: 'IntegrationCode.PositionBottomLeft' },
    { value: 'center-right', label: 'IntegrationCode.PositionCenterRight' },
    { value: 'center-left', label: 'IntegrationCode.PositionCenterLeft' }
  ];
  fabShapeOptions = [
    { value: 'pill', label: 'Pill' },
    { value: 'soft', label: 'Soft' }
  ];
  widgetConfig = {
    theme: 'trattoria',
    accentColor: '#B8853A',
    primaryColor: '#14110D',
    locale: 'de',
    fabPosition: 'bottom-right',
    fabShape: 'pill',
    fabLabel: 'Tisch reservieren',
    fabBgColor: '#14110D'
  };
  embedType = 'esm';
  triggerMode = 'fab';
  useFabBgColor = false;
  qrCodeClassic = 'IIFE <script src=…/bundle.min.js> + element';
  qrCodeModern = 'ESM <script type="module" src=…/bundle.esm.js> + element';
  qrCodeOpenRef = 'widget.open({ centered }) / widget.close()';
  qrCodeOpenEvent = "dispatchEvent(new CustomEvent('gd-open-reservation', {detail:{centered:true}}))";
  qrCodeRestyle = 'theme attribute, accent-color / primary-color, or --res-* CSS vars';
  private hasSavedLocale = false;
  private cachedPreviewKey: string | null = null;
  private cachedPreviewSrcdoc: SafeHtml | null = null;
  isAdmin = false;
  previewHeight = 560;

  @ViewChild('secondaryFontPicker', { static: false }) secondaryFontPicker: FontPickerDirective;
  @ViewChild('previewFrame', { static: false }) previewFrame: ElementRef<HTMLIFrameElement>;

  allowedClientIdWidget = [1, 214427];

  private ngUnsubscribe: Subject<any> = new Subject();
  private fabPreviewTimer: any = null;
  hasWidgetPermission: boolean = false;
  // formBackgroundColorCheckBox: boolean = false;

  constructor(
    public clientResolver: ClientResolverService,
    private formBuilder: FormBuilder,
    private reservationService: ReservationService,
    private snackBar: MatSnackBar,
    private credentialsService: CredentialsService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {
    this.isAdmin = this.credentialsService.isAdmin();
    this.activeClient = this.clientResolver.client;
    this.frontendUrl = environment.frontendUrl;
    this.widgetUrl = `${this.frontendUrl}/widgets/elements.js`;
    this.settingsFormGroup = this.formBuilder.group({
      mainColor: ['#000000', Validators.required],
      primaryColor: ['#009688', Validators.required],
      backgroundColor: ['#ffffff', Validators.required],
      primaryFont: ['Nunito Sans', Validators.required],
      secondaryFont: ['Nunito Sans', Validators.required],
      formBackgroundColor: [null, Validators.required],
      formBackgroundColorCheckBox: [false, ''],
      showOpeningHours: [true, '']
    });
  }

  ngOnInit() {
    this.getIntegrationStyles();
    this.getResSettings();
    if (this.activeClient.id) {
      this.hasWidgetPermission = this.allowedClientIdWidget.includes(this.activeClient.id);
    }
  }

  ngOnDestroy(): any {
    if (this.fabPreviewTimer) {
      clearTimeout(this.fabPreviewTimer);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updatePreviewHeight();
  }

  updatePreviewHeight() {
    this.previewHeight = 560;
  }

  expandPanel(panel: string) {
    this.expandedPanel = panel;
  }

  getIntegrationStyles() {
    this.reservationService
      .getWidgetStyle()
      .takeUntil(this.ngUnsubscribe)
      .subscribe((settings: any) => {
        if (settings) {
          this.settingsFormGroup.get('mainColor').setValue(settings.mainColor);
          this.settingsFormGroup.get('primaryColor').setValue(settings.primaryColor);
          this.settingsFormGroup.get('backgroundColor').setValue(settings.backgroundColor);
          this.settingsFormGroup.get('primaryFont').setValue(settings.primaryFont);
          this.settingsFormGroup.get('secondaryFont').setValue(settings.secondaryFont);
          this.settingsFormGroup.get('formBackgroundColor').setValue(settings.formBackgroundColor);
          if (this.settingsFormGroup.get('formBackgroundColor').value === null) {
            this.settingsFormGroup.get('formBackgroundColorCheckBox').setValue(false);
          } else {
            this.settingsFormGroup.get('formBackgroundColorCheckBox').setValue(true);
          }
          this.settingsFormGroup.get('showOpeningHours').setValue(settings.showOpeningHours !== false);

          if (settings.embedTheme) {
            this.widgetConfig.theme = settings.embedTheme;
          }
          if (settings.embedAccentColor) {
            this.widgetConfig.accentColor = settings.embedAccentColor;
          }
          if (settings.embedPrimaryColor) {
            this.widgetConfig.primaryColor = settings.embedPrimaryColor;
          }
          if (settings.embedLocale) {
            this.widgetConfig.locale = settings.embedLocale;
            this.hasSavedLocale = true;
          }
          if (settings.embedFabPosition) {
            this.widgetConfig.fabPosition = settings.embedFabPosition;
          }
          if (settings.embedFabShape) {
            this.widgetConfig.fabShape = settings.embedFabShape;
          }
          if (settings.embedFabLabel) {
            this.widgetConfig.fabLabel = settings.embedFabLabel;
          }
          if (settings.embedFabBgColor) {
            this.widgetConfig.fabBgColor = settings.embedFabBgColor;
            this.useFabBgColor = true;
          }
          if (settings.embedType) {
            this.embedType = settings.embedType;
          }
          if (settings.embedTriggerMode) {
            this.triggerMode = settings.embedTriggerMode;
          }
        }
      });
  }

  saveIntegrationStyles() {
    this.reservationService
      .saveWidgetStyle(this.settingsFormGroup.value)
      .takeUntil(this.ngUnsubscribe)
      .subscribe((settings: any) => {
        if (settings) {
          this.snackBar.open('Einstellungen wurden gespeichert', '', {
            duration: 2000,
            panelClass: ['snackbar-success']
          });
        }
      });
  }

  checkedHandler(event: any) {
    const checked = event.checked;
    if (checked === true) {
      this.settingsFormGroup.get('formBackgroundColor').setValue('transparent');
    } else {
      this.settingsFormGroup.get('formBackgroundColor').setValue(null);
    }
  }

  getResSettings() {
    this.reservationService
      .getSettings()
      .takeUntil(this.ngUnsubscribe)
      .subscribe((settings: any) => {
        this.generalResSettings = settings;
        if (!this.hasSavedLocale && settings && settings.defaultLang) {
          const match = this.localeOptions.find(o => o.value === settings.defaultLang);
          this.widgetConfig.locale = match ? match.value : 'de';
        }
      });
  }

  saveResSettings() {
    this.reservationService
      .saveSettings(this.generalResSettings)
      .takeUntil(this.ngUnsubscribe)
      .subscribe((resp: any) => {
        this.snackBar.open('Einstellungen erfolgreich aktualisiert', '', {
          duration: 2000,
          panelClass: ['snackbar-success']
        });
      });
  }

  changeBgColor(e: any) {
    this.settingsFormGroup.controls['backgroundColor'].setValue(e);
  }

  changePrimaryColor(e: any) {
    this.settingsFormGroup.controls['primaryColor'].setValue(e);
  }

  changeMainColor(e: any) {
    this.settingsFormGroup.controls['mainColor'].setValue(e);
  }

  changePrimaryFont(e: any) {
    console.log(e);
    this.settingsFormGroup.controls['primaryFont'].setValue(e.family);
    this.secondaryFontPicker.closeDialog();
  }

  changeSecondaryFont(e: any) {
    this.settingsFormGroup.controls['secondaryFont'].setValue(e.family);
    this.secondaryFontPicker.closeDialog();
  }

  get previewSrcdoc(): SafeHtml {
    const clientId = this.activeClient && this.activeClient.id ? this.activeClient.id : 0;
    const isButton = this.triggerMode === 'button';
    const key = clientId + '|' + this.triggerMode + '|' + this.embedType;
    if (this.cachedPreviewSrcdoc && this.cachedPreviewKey === key) {
      return this.cachedPreviewSrcdoc;
    }
    const previewScript =
      this.embedType === 'iife'
        ? `<script src="${this.widgetCdnUrlClassic}"></script>`
        : `<script type="module" src="${this.widgetCdnUrl}"></script>`;
    const widgetEl =
      `<gd-reservation-widget` +
      ` client-id="${clientId}"` +
      ` theme="${this.widgetConfig.theme}"` +
      ` accent-color="${this.widgetConfig.accentColor}"` +
      ` primary-color="${this.widgetConfig.primaryColor}"` +
      ` locale="${this.widgetConfig.locale}"` +
      ` fab-position="${this.widgetConfig.fabPosition}"` +
      ` fab-shape="${this.widgetConfig.fabShape}"` +
      ` fab-label="${this.widgetConfig.fabLabel}"` +
      (this.useFabBgColor ? ` fab-bg-color="${this.widgetConfig.fabBgColor}"` : '') +
      (isButton ? ` hide-fab` : '') +
      `></gd-reservation-widget>`;
    const demoButton = isButton
      ? `<div class="gd-demo-wrap"><button id="gd-demo-trigger" class="gd-demo-btn" ` +
        `onclick="window.dispatchEvent(new CustomEvent('gd-open-reservation',{detail:{centered:true}}))">` +
        `${this.widgetConfig.fabLabel}</button></div>`
      : '';
    // Floating mode auto-opens the modal. Button mode instead injects a style into
    // the widget's shadow root to hide the FAB launcher (preview-only), so only the
    // sample button shows.
    const openScript = isButton
      ? `<script>(function(){var css='.fab-main,.fab-link,.fab-mark,.fab-divider,.fab-label{display:none!important}';` +
        `var n=0,iv=setInterval(function(){n++;var el=document.querySelector('gd-reservation-widget');` +
        `var sr=el&&el.shadowRoot;if(sr){if(!sr.getElementById('gd-hide-fab')){var s=document.createElement('style');` +
        `s.id='gd-hide-fab';s.textContent=css;sr.appendChild(s);}clearInterval(iv);}else if(n>60){clearInterval(iv);}},150);})();</script>`
      : `<script>customElements.whenDefined('gd-reservation-widget').then(function(){` +
        `var n=0,iv=setInterval(function(){n++;var w=document.querySelector('gd-reservation-widget');` +
        `if(w&&typeof w.open==='function'){try{w.open({centered:true});}catch(e){}clearInterval(iv);}` +
        `else if(n>30){clearInterval(iv);}},150);});</script>`;
    const html =
      `<!doctype html><html><head><meta charset="utf-8">` +
      `<style>html,body{margin:0;height:100%;background:transparent;}` +
      `.gd-demo-wrap{display:flex;align-items:center;justify-content:center;height:100%;}` +
      `.gd-demo-btn{font:600 15px system-ui,Arial,sans-serif;padding:12px 22px;border:0;border-radius:8px;` +
      `cursor:pointer;color:#fff;background:${this.widgetConfig.primaryColor};}</style>` +
      `${previewScript}</head><body>` +
      widgetEl +
      demoButton +
      openScript +
      `</body></html>`;
    this.cachedPreviewKey = key;
    this.cachedPreviewSrcdoc = this.sanitizer.bypassSecurityTrustHtml(html);
    return this.cachedPreviewSrcdoc;
  }

  applyPreviewConfig() {
    const frame = this.previewFrame && this.previewFrame.nativeElement;
    const doc = frame && frame.contentDocument;
    const el = doc && doc.querySelector('gd-reservation-widget');
    if (!el) {
      return;
    }
    el.setAttribute('theme', this.widgetConfig.theme);
    el.setAttribute('accent-color', this.widgetConfig.accentColor);
    el.setAttribute('primary-color', this.widgetConfig.primaryColor);
    el.setAttribute('locale', this.widgetConfig.locale);
    el.setAttribute('fab-position', this.widgetConfig.fabPosition);
    el.setAttribute('fab-shape', this.widgetConfig.fabShape);
    el.setAttribute('fab-label', this.widgetConfig.fabLabel);
    if (this.useFabBgColor) {
      el.setAttribute('fab-bg-color', this.widgetConfig.fabBgColor);
    } else {
      el.removeAttribute('fab-bg-color');
    }
    if (this.triggerMode === 'button') {
      el.setAttribute('hide-fab', '');
    } else {
      el.removeAttribute('hide-fab');
    }
    const demoBtn = doc && doc.getElementById('gd-demo-trigger');
    if (demoBtn) {
      demoBtn.textContent = this.widgetConfig.fabLabel;
      (demoBtn as HTMLElement).style.background = this.widgetConfig.primaryColor;
    }
  }

  onPreviewLoad() {
    this.updatePreviewHeight();
    this.applyPreviewConfig();
  }

  private previewWidgetEl(): any {
    const frame = this.previewFrame && this.previewFrame.nativeElement;
    const doc = frame && frame.contentDocument;
    return doc ? doc.querySelector('gd-reservation-widget') : null;
  }

  private openPreviewModal() {
    const frame = this.previewFrame && this.previewFrame.nativeElement;
    const el = this.previewWidgetEl();
    try {
      if (el && typeof el.open === 'function') {
        el.open({ centered: true });
      } else if (frame && frame.contentWindow) {
        frame.contentWindow.dispatchEvent(new CustomEvent('gd-open-reservation', { detail: { centered: true } }));
      }
    } catch (e) {}
  }

  private closePreviewModal() {
    const frame = this.previewFrame && this.previewFrame.nativeElement;
    const el = this.previewWidgetEl();
    try {
      if (el && typeof el.close === 'function') {
        el.close();
      } else if (frame && frame.contentWindow) {
        frame.contentWindow.dispatchEvent(new Event('gd-close-reservation'));
      }
    } catch (e) {}
  }

  onFabChange() {
    this.applyPreviewConfig();
    if (this.triggerMode !== 'fab') {
      return;
    }
    this.closePreviewModal();
    if (this.fabPreviewTimer) {
      clearTimeout(this.fabPreviewTimer);
    }
    this.fabPreviewTimer = setTimeout(() => this.openPreviewModal(), 2500);
  }

  get integrationCode(): string {
    const clientId = this.activeClient && this.activeClient.id ? this.activeClient.id : '';
    const scriptTag =
      this.embedType === 'iife'
        ? `<script src="${this.widgetCdnUrlClassic}"></script>\n`
        : `<script type="module" src="${this.widgetCdnUrl}"></script>\n`;
    const fabBgLine = this.useFabBgColor ? `  fab-bg-color="${this.widgetConfig.fabBgColor}"\n` : '';
    // In "your own button" mode, hide the widget's floating button so only the
    // custom button triggers it.
    const hideFabLine = this.triggerMode === 'button' ? `  hide-fab\n` : '';
    const element =
      scriptTag +
      `<gd-reservation-widget\n` +
      `  client-id="${clientId}"\n` +
      `  theme="${this.widgetConfig.theme}"\n` +
      `  accent-color="${this.widgetConfig.accentColor}"\n` +
      `  primary-color="${this.widgetConfig.primaryColor}"\n` +
      `  locale="${this.widgetConfig.locale}"\n` +
      `  fab-position="${this.widgetConfig.fabPosition}"\n` +
      `  fab-shape="${this.widgetConfig.fabShape}"\n` +
      `  fab-label="${this.widgetConfig.fabLabel}"\n` +
      fabBgLine +
      hideFabLine +
      `></gd-reservation-widget>`;
    if (this.triggerMode === 'button') {
      return (
        element +
        `\n\n` +
        `<button type="button"\n` +
        `  onclick="window.dispatchEvent(new CustomEvent('gd-open-reservation', { detail: { centered: true } }))">\n` +
        `  ${this.widgetConfig.fabLabel}\n` +
        `</button>`
      );
    }
    return element;
  }

  private get refClientId(): string | number {
    return this.activeClient && this.activeClient.id ? this.activeClient.id : '214427';
  }

  get refEmbedClassic(): string {
    return (
      `<script src="${this.widgetCdnUrlClassic}"></script>\n` +
      `<gd-reservation-widget client-id="${this.refClientId}" theme="trattoria" locale="de"></gd-reservation-widget>`
    );
  }

  get refEmbedModern(): string {
    return (
      `<script type="module" src="${this.widgetCdnUrl}"></script>\n` +
      `<gd-reservation-widget client-id="${this.refClientId}"></gd-reservation-widget>`
    );
  }

  get refImperative(): string {
    return (
      `const widget = document.querySelector('gd-reservation-widget');\n` +
      `widget.open();                     // open centered modal (default)\n` +
      `widget.open({ centered: true });   // explicit centered overlay\n` +
      `widget.open({ centered: false });  // anchored near the FAB instead\n` +
      `widget.close();                    // close the modal`
    );
  }

  get refDeclarative(): string {
    return (
      `// Open (centered by default)\n` +
      `window.dispatchEvent(new Event('gd-open-reservation'));\n` +
      `// Open with explicit centering control\n` +
      `window.dispatchEvent(new CustomEvent('gd-open-reservation', { detail: { centered: true } }));\n` +
      `// Close\n` +
      `window.dispatchEvent(new Event('gd-close-reservation'));`
    );
  }

  get refExampleButton(): string {
    return (
      `<button type="button"\n` +
      `  onclick="window.dispatchEvent(new CustomEvent('gd-open-reservation', { detail: { centered: true } }))">\n` +
      `  Book a table now\n` +
      `</button>`
    );
  }

  get refTheming(): string {
    return `gd-reservation-widget {\n  --res-accent: #c0392b;\n  --res-primary: #1f6b3b;\n  --res-radius: 12px;\n}`;
  }

  saveWidgetConfig() {
    const payload = {
      ...this.settingsFormGroup.value,
      embedTheme: this.widgetConfig.theme,
      embedAccentColor: this.widgetConfig.accentColor,
      embedPrimaryColor: this.widgetConfig.primaryColor,
      embedLocale: this.widgetConfig.locale,
      embedFabPosition: this.widgetConfig.fabPosition,
      embedFabShape: this.widgetConfig.fabShape,
      embedFabLabel: this.widgetConfig.fabLabel,
      embedFabBgColor: this.useFabBgColor ? this.widgetConfig.fabBgColor : null,
      embedType: this.embedType,
      embedTriggerMode: this.triggerMode
    };
    this.reservationService
      .saveWidgetStyle(payload)
      .takeUntil(this.ngUnsubscribe)
      .subscribe(() => {
        this.hasSavedLocale = true;
        this.snackBar.open(this.translate.instant('IntegrationCode.SettingsSaved'), '', {
          duration: 2000,
          panelClass: ['snackbar-success']
        });
      });
  }

  copyIntegrationCode() {
    this.copyText(this.integrationCode);
  }

  copyText(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.snackBar.open(this.translate.instant('IntegrationCode.CodeCopied'), '', {
          duration: 2000,
          panelClass: ['snackbar-success']
        });
      })
      .catch(() => {
        this.snackBar.open(this.translate.instant('IntegrationCode.CodeCopyFailed'), '', {
          duration: 2000,
          panelClass: ['snackbar-error']
        });
      });
  }
}
